import React, { useState, useEffect } from 'react'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { Button, Alert, Spin } from 'antd'
import { createSubscription } from 'HttpRequests/billing'

const stripePromise = loadStripe(window.env.STRIPE_KEY)

const planDetails = {
    startup:    { label: 'Startup',    monthly: '£25/mo',   annual: '£250/yr' },
    grow:       { label: 'Grow',       monthly: '£50/mo',   annual: '£500/yr' },
    scale:      { label: 'Scale',      monthly: '£99/mo',   annual: '£990/yr' },
    enterprise: { label: 'Enterprise', monthly: '£199/mo',  annual: '£1,990/yr' },
}

const discountLabels = {
    '30DAYSFREE':    '30 Days Free',
    'JOHNSONS1MNTH': '1 Month Free',
    'LEADPM10':      '10% Off',
    'LEADPM20':      '20% Off',
}

const SuccessScreen = ({ onContinue }) => {
    useEffect(() => {
        const timer = setTimeout(onContinue, 4000)
        return () => clearTimeout(timer)
    }, [])

    return (
        <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <h2 style={{ margin: '0 0 8px' }}>You're all set! 🎉</h2>
            <p style={{ color: '#555', fontSize: 16, margin: '0 0 24px' }}>
                Thank you — your subscription has started!
            </p>
            <Button type="primary" onClick={onContinue} style={{ height: 44, minWidth: 160 }}>
                Go to LeadPM
            </Button>
        </div>
    )
}

const CheckoutForm = ({ billingInfo, onSuccess }) => {
    const stripe   = useStripe()
    const elements = useElements()
    const [loading, setLoading] = useState(false)
    const [error,   setError]   = useState(null)
    const [success, setSuccess] = useState(false)

    if (success) {
        return <SuccessScreen onContinue={onSuccess} />
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        if (!stripe || !elements) return

        setLoading(true)
        setError(null)

        const { error: confirmError, setupIntent } = await stripe.confirmSetup({
            elements,
            redirect: 'if_required',
        })

        if (confirmError) {
            setError(confirmError.message)
            setLoading(false)
            return
        }

        try {
            await createSubscription({ data: { payment_method: setupIntent.payment_method } })
            setSuccess(true)
        } catch (err) {
            setError((err.response && err.response.data && err.response.data.error) || 'Something went wrong. Please try again.')
            setLoading(false)
        }
    }

    const plan    = planDetails[billingInfo.plan] || { label: billingInfo.plan, monthly: '', annual: '' }
    const isAnnual = billingInfo.billing_period === 'annual'
    const price   = isAnnual ? plan.annual : plan.monthly

    return (
        <form onSubmit={handleSubmit} style={{ width: '100%' }}>
            <div style={{
                background: '#f9f9f9',
                border: '1px solid #e8e8e8',
                borderRadius: 6,
                padding: '14px 16px',
                marginBottom: 24,
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: billingInfo.discount_code ? 8 : 0 }}>
                    <div>
                        <div style={{ fontWeight: 600, fontSize: 15 }}>LeadPM {plan.label}</div>
                        <div style={{ color: '#888', fontSize: 12 }}>{isAnnual ? 'Annual' : 'Monthly'} subscription</div>
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 18, color: '#1890ff' }}>{price}</div>
                </div>
                {billingInfo.discount_code && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                        <span style={{ color: '#555', fontSize: 13 }}>Discount applied</span>
                        <strong style={{ color: '#52c41a', fontSize: 13 }}>
                            {billingInfo.discount_code} &mdash; {discountLabels[billingInfo.discount_code] || billingInfo.discount_code}
                        </strong>
                    </div>
                )}
            </div>

            <PaymentElement />

            {error && (
                <Alert type="error" message={error} style={{ marginTop: 16 }} showIcon />
            )}

            <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                disabled={!stripe}
                block
                style={{ marginTop: 24, height: 44 }}
            >
                Set Up Subscription
            </Button>
        </form>
    )
}

const BillingSetup = ({ onSuccess }) => {
    const billingInfo = (window.state && window.state.user && window.state.user.billing_info) || {}

    if (!billingInfo.client_secret) {
        return <Spin tip="Loading payment details..." style={{ display: 'block', marginTop: 80 }} />
    }

    const options = {
        clientSecret: billingInfo.client_secret,
        appearance: { theme: 'stripe' },
    }

    return (
        <div style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#f5f5f5',
        }}>
            <div style={{
                background: '#fff',
                borderRadius: 8,
                padding: 40,
                width: '100%',
                maxWidth: 480,
                boxShadow: '0 2px 16px rgba(0,0,0,0.10)',
            }}>
                <div style={{ textAlign: 'center', marginBottom: 32 }}>
                    <img src="/img/lead-pm-logo.png" alt="LeadPM" style={{ height: 48, marginBottom: 16 }} />
                    <h2 style={{ margin: 0 }}>Set Up Your Subscription</h2>
                    <p style={{ color: '#888', marginTop: 8 }}>
                        Enter your payment details to activate your account.
                    </p>
                </div>

                <Elements stripe={stripePromise} options={options}>
                    <CheckoutForm billingInfo={billingInfo} onSuccess={onSuccess} />
                </Elements>

                <p style={{ textAlign: 'center', marginTop: 24, fontSize: 12, color: '#aaa' }}>
                    Payments secured by Stripe. To cancel, email{' '}
                    <a href="mailto:cancel@leadpm.cloud">cancel@leadpm.cloud</a>
                </p>
            </div>
        </div>
    )
}

export default BillingSetup
